import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { FiRefreshCw, FiSearch, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import {
	shopClient,
	eurCents,
	fmtDate,
	type ShopOrderListItem,
	type OrderStatus,
} from "./shopClient";

const STATUS_LABELS: Record<OrderStatus, string> = {
	Pending: "En attente",
	Paid: "Payée",
	Fulfilled: "Expédiée",
	Refunded: "Remboursée",
	Cancelled: "Annulée",
};

type Tone = "emerald" | "amber" | "slate" | "rose";

const STATUS_TONE: Record<OrderStatus, Tone> = {
	Paid: "emerald",
	Fulfilled: "emerald",
	Pending: "amber",
	Refunded: "slate",
	Cancelled: "rose",
};

const TONE_CLASSES: Record<Tone, string> = {
	emerald: "bg-emerald-50 text-emerald-700",
	amber: "bg-amber-50 text-amber-700",
	slate: "bg-slate-100 text-slate-600",
	rose: "bg-rose-50 text-rose-600",
};

function StatusBadge({ status }: { status: OrderStatus }) {
	const tone = STATUS_TONE[status] ?? "slate";
	return (
		<span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_CLASSES[tone]}`}>
			{STATUS_LABELS[status] ?? status}
		</span>
	);
}

const LIMIT = 50;
const STATUS_OPTIONS: OrderStatus[] = ["Pending", "Paid", "Fulfilled", "Refunded", "Cancelled"];

export default function Orders() {
	const history = useHistory();

	const [status, setStatus] = useState<string>("");
	const [searchInput, setSearchInput] = useState<string>("");
	const [q, setQ] = useState<string>("");
	const [page, setPage] = useState<number>(1);

	const [items, setItems] = useState<ShopOrderListItem[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	const totalPages = useMemo(() => Math.max(1, Math.ceil(total / LIMIT)), [total]);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await shopClient.orders.list({
				status: status || undefined,
				q: q.trim() || undefined,
				page,
				limit: LIMIT,
			});
			setItems(res.items);
			setTotal(res.total);
		} catch (e: any) {
			setError(e?.message ?? "Erreur");
			setItems([]);
			setTotal(0);
		} finally {
			setLoading(false);
		}
	}, [status, q, page]);

	useEffect(() => {
		void load();
	}, [load]);

	const onStatusChange = (value: string) => {
		setStatus(value);
		setPage(1);
	};

	const onSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setPage(1);
		setQ(searchInput.trim());
	};

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full max-w-6xl space-y-6">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Boutique</p>
						<h1 className="text-2xl font-semibold text-slate-900">Commandes</h1>
						<p className="mt-1 text-sm text-slate-500">
							Suivi des commandes, expéditions et remboursements.
						</p>
					</div>
					<button
						type="button"
						onClick={() => void load()}
						disabled={loading}
						className="ml-auto inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
					>
						<FiRefreshCw className={loading ? "animate-spin" : ""} />
						Actualiser
					</button>
				</div>

				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-sm">
					<select
						value={status}
						onChange={(e) => onStatusChange(e.target.value)}
						className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
					>
						<option value="">Tous</option>
						{STATUS_OPTIONS.map((s) => (
							<option key={s} value={s}>
								{STATUS_LABELS[s]}
							</option>
						))}
					</select>

					<form onSubmit={onSearch} className="flex flex-1 items-center gap-2">
						<input
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							placeholder="Rechercher # / email / nom / suivi…"
							className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
						/>
						<button
							type="submit"
							className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
						>
							<FiSearch />
							Rechercher
						</button>
					</form>
				</div>

				{error && (
					<div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
						{error}
					</div>
				)}

				<div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-slate-100 text-sm">
							<thead className="bg-slate-50">
								<tr>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">#</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Date</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Client</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Statut</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Articles</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Total</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{loading ? (
									<tr>
										<td colSpan={6} className="px-5 py-10 text-center text-slate-400">
											Chargement…
										</td>
									</tr>
								) : items.length === 0 ? (
									<tr>
										<td colSpan={6} className="px-5 py-10 text-center text-slate-400">
											Aucune commande
										</td>
									</tr>
								) : (
									items.map((o) => (
										<tr
											key={o.id}
											onClick={() => history.push("/orders/" + o.id)}
											className="cursor-pointer hover:bg-slate-50"
										>
											<td className="px-5 py-3">
												<Link
													to={"/orders/" + o.id}
													onClick={(e) => e.stopPropagation()}
													className="font-mono font-medium text-slate-900 hover:underline"
												>
													{o.id.slice(0, 8).toUpperCase()}
												</Link>
											</td>
											<td className="px-5 py-3 text-slate-600">{fmtDate(o.createdAt)}</td>
											<td className="px-5 py-3 text-slate-700">
												{o.customerName || o.customerEmail || "Invité"}
											</td>
											<td className="px-5 py-3">
												<StatusBadge status={o.status} />
											</td>
											<td className="px-5 py-3 text-slate-600">{o.itemCount}</td>
											<td className="px-5 py-3">
												<div className="font-medium text-slate-900">
													{eurCents(o.totalCents, o.currency)}
												</div>
												{o.refundedTotalCents > 0 && (
													<div className="text-xs text-rose-600">
														remb. {eurCents(o.refundedTotalCents, o.currency)}
													</div>
												)}
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>

					<div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 text-sm text-slate-500">
						<span>
							{total} commande{total > 1 ? "s" : ""}
						</span>
						<div className="flex items-center gap-3">
							<span>
								page {page} / {totalPages}
							</span>
							<button
								type="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={loading || page <= 1}
								className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
							>
								<FiChevronLeft />
								Précédent
							</button>
							<button
								type="button"
								onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
								disabled={loading || page >= totalPages}
								className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
							>
								Suivant
								<FiChevronRight />
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
